import { useState } from 'react'
import { useQuery } from 'react-query'
import { Checkbox, Chip, FormControlLabel, Typography } from '@material-ui/core'
import { findClients } from 'api/clients'
import InformationTable from 'components/InformationTable/InformationTable'
import SearchInput from 'elements/SearchInput/SearchInput'
import QuickAccessButton from 'elements/QuickAccessButton/QuickAccessButton'
import QuickAccessModal from 'elements/QuickAccessModal/QuickAccessModal'
import { useMessageSnackbarActions } from 'elements/MessageContext/MessageContext'
import Enlace from 'elements/Enlace/Enlace'
import WrapperLink from 'elements/WrapperLink/WrapperLink'
import {
  AccessTypeLabels,
  AccessTypes,
  RelativePathByAccessType
} from 'shared/constants'
import StatusIcon from 'shared/StatusIcon'
import { textFilter } from 'utils/table'
import { downloadCSVFile, getCSVContent } from 'utils/csv'
import styles from './ListadoClientes.module.css'

const ColorByAccessType = {
  [AccessTypes.STUDENT]: 'primary',
  [AccessTypes.TRIAL]: 'secondary'
}

const ListadoClientes = () => {
  const [search, setSearch] = useState('')
  const [showMore, setShowMore] = useState(false)
  const [showQuickAccessModal, setShowQuickAccessModal] = useState(false)
  const [quickAccessUrl, setQuickAccessUrl] = useState('')
  const [selectedClientName, setSelectedClientName] = useState('')
  const { setErrorMessage } = useMessageSnackbarActions()

  const { isFetching, data: clients = [] } = useQuery(
    ['client-list'],
    findClients,
    {
      refetchOnWindowFocus: false,
      onError(e) {
        setErrorMessage()
      }
    }
  )
  const filterSearch = subject => {
    return textFilter({
      object: subject,
      fields: ['cliente.name', 'email', 'cliente.phone'],
      search: search
    })
  }

  const handleExport = () => {
    const content = getCSVContent({
      columns: ['Nombre', 'E-mail', 'Tipo'],
      data: clients,
      transform: c => [
        c.cliente.name,
        c.email,
        AccessTypeLabels[c.cliente.accessType]
      ]
    })
    downloadCSVFile(content, 'clientes')
  }

  const handleCloseModal = () => {
    setShowQuickAccessModal(false)
    setQuickAccessUrl('')
    setSelectedClientName('')
  }

  const handleQuickAccessGenerated = (url, clientName) => {
    setQuickAccessUrl(url)
    setSelectedClientName(clientName)
    setShowQuickAccessModal(true)
  }

  return (
    <div>
      <div className={styles.toolbar}>
        <SearchInput
          value={search}
          onChange={e => setSearch(e.target.value)}
          autoFocus
        />
        <Typography variant='button' color='textSecondary'>
          {clients.length} clientes
        </Typography>
        <div className={styles.actions}>
          <FormControlLabel
            control={
              <Checkbox
                checked={showMore}
                onChange={() => setShowMore(state => !state)}
              />
            }
            label='Ver más datos'
          />
          <div className={styles.download} onClick={handleExport}>
            <i className='material-icons'>download</i>
          </div>
        </div>
      </div>

      <InformationTable
        className={styles.table}
        isFetching={isFetching}
        details={[
          { title: 'Nombre', key: 'name', align: 'left' },
          {
            title: 'Email',
            key: 'email',
            align: 'left'
          },
          {
            title: 'Tipo',
            key: 'accessType',
            sortDisabled: true
          },
          { title: 'Email Verificado', key: 'email_verificado' },
          { title: 'Activo', key: 'activo' },
          { title: 'Permisos', key: 'permissions' },
          {
            title: '',
            key: 'options',
            align: 'center',
            sortDisabled: true
          }
        ].filter(d => {
          if (!showMore)
            return !['email_verificado', 'activo', 'permissions'].includes(
              d.key
            )
          return d
        })}
        data={clients
          .filter(filterSearch)
          .map(
            ({
              email,
              email_verificado,
              acceso_classfy,
              acceso_app,
              acceso_barbeibot,
              activo,
              cliente = {}
            }) => ({
              email: email,
              options: (
                <div className={styles.optionsContainer}>
                  <Enlace
                    href={`https://agora.mundoestudiante.cloud/${
                      RelativePathByAccessType[cliente.accessType]
                    }/${cliente.id}/ficha`}
                    target='_blank'
                  >
                    <i className='material-icons'>remove_red_eye</i>
                  </Enlace>
                  <QuickAccessButton
                    clientId={cliente.id}
                    clientName={cliente.name}
                    onQuickAccessGenerated={url =>
                      handleQuickAccessGenerated(url, cliente.name)
                    }
                    disabled={!cliente.id}
                    size='small'
                  />
                </div>
              ),
              name: (
                <WrapperLink to={`/clients/${cliente.id}`} color='secondary'>
                  {cliente.name || '--'}
                </WrapperLink>
              ),
              accessType: (
                <Chip
                  label={AccessTypeLabels[cliente.accessType]}
                  color={ColorByAccessType[cliente.accessType]}
                />
              ),
              email_verificado: <StatusIcon active={email_verificado} />,
              activo: <StatusIcon active={activo} />,
              permissions: (
                <div className={styles.permissions}>
                  {acceso_classfy && (
                    <Chip label='Classfy' size='small' color='secondary' />
                  )}
                  {acceso_app && (
                    <Chip label='App' size='small' color='secondary' />
                  )}
                  {acceso_barbeibot && (
                    <Chip label='Babeibot' size='small' color='secondary' />
                  )}
                </div>
              )
            })
          )}
        size='small'
      />

      <QuickAccessModal
        open={showQuickAccessModal}
        quickAccessUrl={quickAccessUrl}
        onClose={handleCloseModal}
        clientName={selectedClientName}
      />
    </div>
  )
}

export default ListadoClientes
